import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';

import Buttons from '../Buttons/Buttons';
import {
  LOGOUT,
  SETTINGS,
  GO_BACK,
  HELP,
  REPORTS,
  SHARE,
  PARENT_ADVICE,
  QUICKSTART_VIDEOS,
  SUGGESTED_LEVELS,
} from 'consts/buttons';
import { logout } from 'actions/authActions';
import {
  showSettings,
  showMenuHelp,
  goBack,
  showFamilyReports,
  showParentAdvice,
  showQuickstartVideos,
} from 'actions/flinkPlayActions';
import {
  checkTeam,
  isMobileAppRuntime,
} from 'utils';

const MenuButtons = ({
  buttons,
  videos,
  showSettings,
  goBack,
  solution,
  solutionMenu,
  groupsBreadcrumbs,
  familyReportsOpened,
  showFamilyReports,
  showParentAdvice,
  showQuickstartVideos,
  parentAdvice,
  parentAdviceUrl,
  quickStartVideos,
  showMenuHelp,
  organizationConfig,
  product,
  family,
  team,
  onSuggestedLevels,
  suggestedLevelsButton,
  forcedButtons,
}) => {
  const dispatch = useDispatch();
  const familyTeam = checkTeam(team, family);
  const mobileRuntime = isMobileAppRuntime();
  const formattedButtons = useMemo(
    () => {
      const sourceButtons = Array.isArray(forcedButtons) ? forcedButtons : buttons;
      const hasSuggestedLevelsButton = sourceButtons.some((btn) => {
        const btnObj = typeof btn === 'string' ? { type: btn } : btn || {};
        return btnObj.type === SUGGESTED_LEVELS;
      });
      const buttonsForRender = suggestedLevelsButton && !hasSuggestedLevelsButton
        ? [...sourceButtons, suggestedLevelsButton]
        : sourceButtons;

      return buttonsForRender.map((btn) => {
        const { activityBuilder } = solution || {};
        const dispatchLogOut = () => dispatch(logout(familyTeam));

        const btnObj = typeof btn === 'string' ? { type: btn } : btn;

        if (btnObj.onClick) {
          return btnObj;
        }

        switch (btnObj.type) {
          case SHARE:
            btnObj.dontShow = !activityBuilder || btnObj.dontShow;
            btnObj.onClick = () => {};

            break;
          case REPORTS:
            btnObj.dontShow =
              mobileRuntime || familyTeam === false || product?.onePerson || organizationConfig.noReports || familyReportsOpened 
              || btnObj.dontShow;
            btnObj.onClick = () => showFamilyReports(true);

            break;
          case GO_BACK: {
            if (
              solutionMenu &&
              solutionMenu.menuGroups &&
              solutionMenu.menuGroups.length === 1 &&
              !groupsBreadcrumbs.length
            ) {
              btnObj.type = LOGOUT;
              btnObj.dontShow = organizationConfig.noLogout;
              btnObj.onClick = dispatchLogOut;
            } else {
              btnObj.onClick = goBack;
            }
            break;
          }
          case LOGOUT:
            btnObj.onClick = dispatchLogOut;
            btnObj.dontShow = organizationConfig.noLogout;
            break;
          case HELP:
            btnObj.onClick = showMenuHelp;
            break;
          case PARENT_ADVICE: {
            btnObj.onClick = () => showParentAdvice(!parentAdvice);
            btnObj.dontShow = btnObj.dontShow || !parentAdviceUrl;
            break;
          }

          case SETTINGS:
            btnObj.dontShow = organizationConfig.noSettings;
            btnObj.onClick = showSettings;
            break;

          case QUICKSTART_VIDEOS:
            btnObj.onClick = () => showQuickstartVideos(!quickStartVideos);
            btnObj.dontShow = product?.onePerson || btnObj.dontShow || !videos.length;
            break;

          case SUGGESTED_LEVELS:
            btnObj.globalButtonAlias = btnObj.globalButtonAlias || REPORTS;
            btnObj.titleStringNumber = btnObj.titleStringNumber || 3708;
            btnObj.titleName = btnObj.titleName || 'Suggested Levels';
            btnObj.onClick = btnObj.onClick || onSuggestedLevels || suggestedLevelsButton?.onClick || (() => {});
            btnObj.dontShow = btnObj.dontShow || !(onSuggestedLevels || suggestedLevelsButton?.onClick);
            if (btnObj.matchFamilyReportsVisibility) {
              btnObj.dontShow = btnObj.dontShow
                || familyTeam === false
                || product?.onePerson
                || organizationConfig.noReports
                || familyReportsOpened;
            }
            break;

          default:
            btnObj.onClick = () => {};
        }

        return btnObj;
      });
    },
    [
      buttons,
      videos,
      dispatch,
      showSettings,
      showMenuHelp,
      solution,
      goBack,
      showFamilyReports,
      groupsBreadcrumbs.length,
      familyReportsOpened,
      solutionMenu,
      parentAdvice,
      parentAdviceUrl,
      showParentAdvice,
      showQuickstartVideos,
      quickStartVideos,
      organizationConfig,
      product,
      familyTeam,
      onSuggestedLevels,
      suggestedLevelsButton,
      forcedButtons,
      mobileRuntime,
    ]
  );

  return <Buttons buttons={formattedButtons} />;
};

MenuButtons.propTypes = {
  buttons: PropTypes.array.isRequired,
  videos: PropTypes.array.isRequired,
  locale: PropTypes.object.isRequired,
  groupsBreadcrumbs: PropTypes.array.isRequired,
  showSettings: PropTypes.func.isRequired,
  showMenuHelp: PropTypes.func.isRequired,
  showFamilyReports: PropTypes.func.isRequired,
  showParentAdvice: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  familyReportsOpened: PropTypes.bool,
  solution: PropTypes.object,
  parentAdvice: PropTypes.bool,
  parentAdviceUrl: PropTypes.string,
  organizationConfig: PropTypes.shape({
    noReports: PropTypes.bool,
    noLogout: PropTypes.bool,
  }),
  onSuggestedLevels: PropTypes.func,
  suggestedLevelsButton: PropTypes.object,
  forcedButtons: PropTypes.array,
};

const mapStateToProps = ({
  flinkPlay: {
    menuButtons,
    solution,
    solutionMenu,
    solutionLocale,
    parentAdvice,
    parentAdviceUrl,
    familyReportsOpened,
    groupsBreadcrumbs,
    learningCenter: { config },
    product,
    family,
    team,
  },
  common,
  status: { lang }
}) => {
  const quickStart = common?.config?.loginPageDocuments?.quickStart || {};
  const localeCode = (solutionLocale || lang)?.code;
  const videos = isMobileAppRuntime()
    ? quickStart[localeCode] || []
    : quickStart?.[localeCode] || [];

  return {
    organizationConfig: config,
    buttons: menuButtons,
    videos,
    locale: solutionLocale || lang,
    solutionMenu,
    groupsBreadcrumbs,
    parentAdvice,
    parentAdviceUrl,
    solution,
    familyReportsOpened,
    product,
    family,
    team,
  };
};

export default compose(
  connect(mapStateToProps, {
    goBack,
    showSettings,
    showMenuHelp,
    showFamilyReports,
    showParentAdvice,
    showQuickstartVideos,
  })
)(MenuButtons);
